import { useEffect, useRef, useState } from 'react';
import { toast } from 'src/components/shadcn/useToast';
import { SavedSearchLookupStatusCode } from '../constants';

interface UseSavedSearchToastsProps {
  lookupStatus?: string;
  searchId?: string | null;
  searchName?: string;
}

export const useSavedSearchToasts = ({
  lookupStatus,
  searchId,
  searchName = 'your search'
}: UseSavedSearchToastsProps) => {
  // Keep track of the in-progress toast so we can dismiss it
  const inProgressToastRef = useRef<{ dismiss: () => void } | null>(null);
  // Track dismissed toasts to prevent re-showing them
  const [dismissedToasts, setDismissedToasts] = useState<Set<string>>(new Set());
  // Track previous status to detect transitions
  const prevStatusRef = useRef<string | null>(null);

  // Show toast when contact lookup status changes
  useEffect(() => {
    if (lookupStatus && searchId) {
      const currentStatus = lookupStatus;
      const previousStatus = prevStatusRef.current;
      const toastKey = `${searchId}-${currentStatus}`;
      
      // Initialize previous status if it's the first time we see this status
      if (previousStatus === null) {
        prevStatusRef.current = currentStatus;
        return;
      }
      
      if (currentStatus === SavedSearchLookupStatusCode.Queued || 
          currentStatus === SavedSearchLookupStatusCode.InProgress) {
        
        // Don't show toast if user has dismissed it for this search/status combination
        if (dismissedToasts.has(toastKey)) {
          // Update previous status and return
          prevStatusRef.current = currentStatus;
          return;
        }
        
        // Dismiss any existing in-progress toast first
        if (inProgressToastRef.current) {
          inProgressToastRef.current.dismiss();
        }
        
        // Show persistent toast for in-progress status
        inProgressToastRef.current = toast({
          title: "Looking up contacts",
          description: (
            <>
              We're busy finding contact information for <strong>{searchName}</strong>. This may take a few minutes.
            </>
          ),
          duration: Infinity, // Persist until manually dismissed
          onOpenChange: (open) => {
            // If toast is being closed by user dismissal, track it
            if (!open && inProgressToastRef.current) {
              setDismissedToasts(prev => new Set(prev).add(toastKey));
              inProgressToastRef.current = null;
            }
          },
        });
      } else if (currentStatus === SavedSearchLookupStatusCode.Complete) {
        // Dismiss the in-progress toast when complete
        if (inProgressToastRef.current) {
          inProgressToastRef.current.dismiss();
          inProgressToastRef.current = null;
        }
        
        // Only show completion toast if we're transitioning INTO complete status
        // (not if we're already in complete status)
        if (previousStatus !== SavedSearchLookupStatusCode.Complete) {
          toast({
            title: "Contact lookup complete",
            description: (
              <>
                We've finished finding contact information for <strong>{searchName}</strong>.
              </>
            ),
            duration: 4000,
            variant: "success",
          });
        }
      }
      
      // Update the previous status for next comparison
      prevStatusRef.current = currentStatus;
    }
  }, [lookupStatus, searchName, searchId, dismissedToasts]);

  // Cleanup: dismiss in-progress toast on unmount
  useEffect(() => {
    return () => {
      if (inProgressToastRef.current) {
        inProgressToastRef.current.dismiss();
      }
    };
  }, []);

  return {
    // Could expose methods here if needed, but for now just internal functionality
  };
};